import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Button,
  Form,
  Alert,
  Radio,
  Space,
  Input,
  Statistic
} from 'antd';
import { multiply, round } from 'mathjs';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { toTitleCase, areDeepEqual } from '../utils';
import CheckoutAlert from './CheckoutAlert';
import { user } from '../../lib/authLib';
import config from '../../config';
import AnimateHeight from 'react-animate-height';
// import { CardElement, injectStripe } from 'react-stripe-elements';
// My Components
import LoaderButton from '../../components/LoaderButton';
import './CheckoutFlow.scss';

let incrementCountUpdatesApplied = 0;
let stripeCardElement;

function CheckoutFlow({
  price, uuid, handleCheckoutModalClose, leadFollowClass, title = ''
}) {
  const [form] = Form.useForm();
  const stripe = useStripe();
  const elements = useElements();
  // Stripe & Credit Card Stuff
  const [isLoading, setIsLoading] = useState(false);
  const defaultAlertVal = { title: '', text: '', type: 'error' }; // error must be set, or antd Alert will explode
  const [showErrorMessage, setShowErrorMessage] = useState(defaultAlertVal);
  /* eslint-disable-next-line no-unused-vars */
  const [isCardComplete, setIsCardComplete] = useState(false);
  /* eslint-disable-next-line no-unused-vars */
  const [showCreditCardError, setShowCreditCardError] = useState(false);
  // Are leads and follows allowed
  const [isAdmitted, setIsAdmitted] = useState([]);
  // successAlert
  const [showPaymentSuccessfulMsg, setShowPaymentSuccessfulMsg] = useState(0);

  const userPropOrEmptyStr = (prop) => user.length > 0 && user[0][prop] ? user[0][prop] : '';
  const defaultInitialValues = [
    {
      name: userPropOrEmptyStr('name'),
      email: userPropOrEmptyStr('email'),
      position: null,
    }
  ];

  async function checkWaitlistAndDancerStatus(applicants, checkUuid, setIsAccepted) {
    const myNm = ++incrementCountUpdatesApplied;

    try {
      const accepted = await window.fetch(
        `${config.serverUrl}classes/check-lead-follow-balance/${checkUuid}`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(applicants
            .filter((attendee) => attendee && 'position' in attendee)
            .map((attendee) => ({ ...attendee, position: attendee.position || "either" }))
          ),
        },
      );

      const leadFollowBalanceResponse = await accepted.json();

      if (myNm === incrementCountUpdatesApplied) {
        const isAdmittedBuilder = applicants.map((attendee) => !(attendee && 'position' in attendee));
        leadFollowBalanceResponse.forEach((acceptedDancer) => {
          isAdmittedBuilder[acceptedDancer.index] = true;
        });
        setIsAdmitted(isAdmittedBuilder);

        if (isAdmittedBuilder.some((x) => !x)) {
          setShowErrorMessage({
            title: 'Lead/Follow Imbalance',
            text: 'Not all students can be admitted due to a Lead/Follow imbalance in the class. Some or all applicants will be added to a waitlist and will not be charged until a position becomes available.',
            type: 'warning'
          });
        } else {
          setShowErrorMessage(defaultAlertVal);
        }

      }
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Error! \n', error);
    }
  }

  async function onFinish(values) {
    // if (!isCardComplete) {
    //  setShowCreditCardError(true);
    //  return;
    // }

    setIsLoading(true);

    let clientSecret;
    const email = user[0].email || values.attendees[0].email;

    try {
      values.attendees = values.attendees.map((attendee) => ({ ...attendee, position: attendee.position || "either" })); // may not be needed? Defensive fix for non lead-follow classes
      clientSecret = (await (await window.fetch(
        `${config.serverUrl}classes/create-payment-intent/${uuid}/${email}`,
        {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
        },
      )).json()).clientSecret;
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Error! \n', error);
    }

    if (!!clientSecret) {
      const payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (payload.error) {
        setShowErrorMessage({
          title: toTitleCase(payload.error.code.replace('_', ' ')).replace(/cvc/ig, c => c.toUpperCase()),
          text: payload.error.message,
          type: 'error'
        });

        setIsLoading(false);
        return;
      } else if (!areDeepEqual(showErrorMessage, defaultAlertVal)) {
        setShowErrorMessage(defaultAlertVal);
      }
      stripeCardElement.clear();
    }

    setIsLoading(false);
    form.setFieldsValue({ attendees: defaultInitialValues });

    setShowPaymentSuccessfulMsg('auto');

    setTimeout(() => {
      setShowPaymentSuccessfulMsg(0);
      handleCheckoutModalClose();
    }, 15 * 1000);
  }

  /* eslint-disable-next-line no-unused-vars */
  function onFinishFailed(values) {
    setShowCreditCardError(!isCardComplete);
  }

  const calcPriceTo2Decimals = round(
    multiply(
      isAdmitted.filter(isAdm => isAdm).length,
      price
    ),
    2
  );

  return (
    <Form
      form={form}
      className="CheckoutFlow"
      name="basic"
      initialValues={{ remember: true }}
      onValuesChange={async (changedVal, allFormInfo) => {
        const { attendees: attendeesChanged } = changedVal;
        let { attendees: attendeesAllForm } = allFormInfo;

        const hasRadioBeenPushed = attendeesChanged && attendeesChanged.some(
          (attendee) => attendee && ('position' in attendee),
        );

        const hasMinusOrAddFieldBeenPushed = attendeesAllForm.length !== isAdmitted.length;

        /* eslint-disable-next-line no-console */
        if (hasRadioBeenPushed || hasMinusOrAddFieldBeenPushed) {
          attendeesAllForm = attendeesAllForm.map((attendee, i) => ({ index: i, ...attendee }));
          checkWaitlistAndDancerStatus( // no await is correct!!!
            attendeesAllForm,
            uuid,
          );
        }
      }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Form.List name="attendees" initialValue={defaultInitialValues}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({
              key, name, fieldKey, ...restField
            }, index) => (
              <Space
                key={key}
                style={{ display: 'flex', marginBottom: 8 }}
                align="baseline"
              >
                <Form.Item
                  {...restField}
                  name={[name, 'name']}
                  fieldKey={[fieldKey, 'name']}
                  rules={[{ required: true, message: 'Missing name' }]}
                >
                  <Input placeholder="Name" />
                </Form.Item>

                <Form.Item
                  {...restField}
                  name={[name, 'email']}
                  fieldKey={[fieldKey, 'email']}
                  rules={[{ required: true, type: 'email', message: 'Invalid email address' }]}
                >
                  <Input placeholder="Email" />
                </Form.Item>

                { leadFollowClass && <Form.Item
                  {...restField}
                  name={[name, 'position']}
                  fieldKey={[fieldKey, 'position']}
                  rules={[{ required: true, message: 'Missing position' }]}
                  initialValue={null}
                >
                  <Radio.Group>
                    <Radio value="lead">Lead</Radio>
                    <Radio value="follow">Follow</Radio>
                  </Radio.Group>
                </Form.Item> }
                <div>
                  {
                    isAdmitted[index] !== false
                      ? `$${(parseFloat(price)).toFixed(2)}`
                      : 'Waitlisted'
                    // TODO fetch isAccepted
                  }
                </div>
                <MinusCircleOutlined onClick={() => remove(name)} />
              </Space>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add Student
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <CheckoutAlert message={showErrorMessage} />

      {
        (!isAdmitted.length || !!isAdmitted.filter(isAdm => isAdm).length) && (
          <CardElement onReady={(element) => stripeCardElement = element} />
        )
      }

      <AnimateHeight
        duration={ 500 }
        height={ showPaymentSuccessfulMsg }
        style={{ marginTop: '30px' }}
      >
        <Alert
          message="Payment Complete"
          description={`Thank you for registering ${title.length > 0 ? `for ${title} ` : ''}at Century Ballroom!`}
          type="success"
          showIcon
        />
      </AnimateHeight>

      <Form.Item className="bottom-register-and-total submit-btn">
        <LoaderButton
          className="register-btn"
          type="submit"
          isLoading={isLoading}
        >
          Register
        </LoaderButton>
        <Statistic
          className="total-cost"
          title="Total"
          prefix="$"
          precision={2}
          value={calcPriceTo2Decimals}
        />
      </Form.Item>
    </Form>
  );
}

export default CheckoutFlow;
